import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  // Free shipping progress bar - real-time updates
const FREE_SHIPPING_THRESHOLD = 5000; // $50.00 in cents

function updateFreeShippingBar(cartTotal) {
  const bar = document.getElementById('free-shipping-bar');
  if (!bar) return;

  const progress = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const remaining = FREE_SHIPPING_THRESHOLD - cartTotal;

  const progressEl = document.getElementById('free-shipping-bar-progress');
  const textEl = document.getElementById('free-shipping-bar-text');

  if (progressEl) progressEl.style.width = `${progress}%`;

  if (textEl) {
    if (cartTotal >= FREE_SHIPPING_THRESHOLD) {
      textEl.innerHTML = `🎉 You've unlocked free shipping!`;
    } else {
      const dollars = Math.floor(remaining / 100);
      const cents = String(remaining % 100).padStart(2, '0');
      textEl.innerHTML = `You're <strong>$${dollars}.${cents}</strong> away from free shipping`;
    }
  }
}

document.addEventListener('cart:update', (event) => {
  const cartTotal = event.detail?.resource?.total_price;
  if (typeof cartTotal === 'number') {
    updateFreeShippingBar(cartTotal);
  }
});
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

